import { cva, type VariantProps } from 'class-variance-authority';
import mergeRefs from 'merge-refs';
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  createContext,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { DialogContentPresenceContext } from '@/components/ui/dialog/dialog';
import { useClickAway } from '@/hooks/useClickAway';
import { cn } from '@/lib/utils';

interface DropdownMenuRefs {
  triggerRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  rootId: string;
  open: boolean;
}
const DropdownMenuRefsContext = createContext<DropdownMenuRefs | null>(null);

const ROOT_ATTR = 'data-dropdown-root-id';
const OPEN_ATTR = 'data-dropdown-open';
const TRIGGER_OPEN_STATE_CLASS_NAME = cn(
  'data-[state=open]:bg-secondary-hover data-[state=open]:text-foreground',
  'data-[dropdown-open=true]:bg-secondary-hover data-[dropdown-open=true]:text-foreground'
);

export function DropdownMenuRoot({
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
  modal,
  ...rest
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root>) {
  // Inside a Dialog, default to non-modal to avoid Radix's body pointer-events conflict.
  const insideDialog = useContext(DialogContentPresenceContext);
  const resolvedModal = modal ?? !insideDialog;

  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const open = isControlled ? controlledOpen : internalOpen;

  const rootId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLElement | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  useClickAway([triggerRef, contentRef], () => handleOpenChange(false), {
    enabled: open,
    // Match only our own portaled descendants (sub-menus), not unrelated menus.
    ignoreSelector: `[${ROOT_ATTR}="${CSS.escape(rootId)}"]`,
  });

  return (
    <DropdownMenuRefsContext.Provider
      value={{ triggerRef, contentRef, rootId, open }}
    >
      <DropdownMenuPrimitive.Root
        {...rest}
        modal={resolvedModal}
        open={open}
        onOpenChange={handleOpenChange}
      />
    </DropdownMenuRefsContext.Provider>
  );
}

export const DropdownMenuGroup = DropdownMenuPrimitive.Group;

export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

export const DropdownMenuSub = DropdownMenuPrimitive.Sub;

export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const triggerVariants = cva(
  cn(
    'flex items-center gap-2 justify-between text-foreground rounded-lg px-3 py-1.5 text-sm',
    TRIGGER_OPEN_STATE_CLASS_NAME
  ),
  {
    variants: {
      variant: {
        default:
          'bg-secondary hover:bg-secondary-hover data-[state=open]:bg-secondary-active data-[dropdown-open=true]:bg-secondary-active',
        ghost:
          'bg-transparent hover:bg-secondary-hover data-[state=open]:bg-secondary-hover data-[dropdown-open=true]:bg-secondary-hover',
        outline:
          'border border-border-strong bg-transparent data-[state=open]:bg-secondary-hover data-[dropdown-open=true]:bg-secondary-hover',
      },
      size: {
        default: 'text-sm',
        sm: 'text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const itemVariants = cva(
  'relative flex select-none items-center gap-3 justify-start text-left rounded-xl px-3 py-2 outline-none focus:outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-3.5 [&>svg]:shrink-0 w-full text-foreground hover:bg-secondary-hover data-[highlighted]:bg-secondary-hover data-[highlighted]:text-foreground rounded-md',
  {
    variants: {
      variant: {
        default: '',
        destructive: 'text-danger-9',
      },
      textSize: {
        xs: 'text-xs',
        default: 'text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      textSize: 'default',
    },
  }
);

export const DropdownMenuTrigger = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> &
    VariantProps<typeof triggerVariants>
>(({ className, variant, size, asChild, ...props }, ref) => {
  const refs = useContext(DropdownMenuRefsContext);
  return (
    <DropdownMenuPrimitive.Trigger
      ref={mergeRefs(
        ref,
        refs?.triggerRef as RefObject<HTMLButtonElement | null> | undefined
      )}
      asChild={asChild}
      className={cn(
        asChild
          ? TRIGGER_OPEN_STATE_CLASS_NAME
          : triggerVariants({ variant, size }),
        className
      )}
      {...{ [ROOT_ATTR]: refs?.rootId }}
      {...{ [OPEN_ATTR]: refs?.open ? 'true' : undefined }}
      {...props}
    />
  );
});

export const DropdownMenuSubTrigger = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'relative flex select-none items-center gap-3 justify-start text-left rounded-xl px-3 py-2 text-sm outline-none focus:outline-none transition-colors',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      '[&>svg]:size-3.5 [&>svg]:shrink-0',
      'w-full text-foreground',
      'hover:bg-secondary-hover data-[highlighted]:bg-secondary-hover data-[state=open]:bg-secondary-hover',
      'rounded-md',
      inset && 'pl-8',
      className
    )}
    {...props}
  >
    {children}
    <Icons.chevronRight className="ml-auto size-4" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

export const DropdownMenuSubContent = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.SubContent>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => {
  const refs = useContext(DropdownMenuRefsContext);
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        ref={ref}
        className={cn(
          'z-50 min-w-[12rem] max-w-[16rem] overflow-hidden rounded-xl bg-surface backdrop-blur-xl p-1 text-foreground shadow-sm',
          'origin-[--radix-dropdown-menu-content-transform-origin]',
          'border border-border-strong',
          'max-h-[300px] overflow-y-auto',
          className
        )}
        {...{ [ROOT_ATTR]: refs?.rootId }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

type DropdownMenuContentProps = ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Content
> & {
  fit?: boolean;
};

export const DropdownMenuContent = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 4, fit = false, onWheel, ...props }, ref) => {
  const refs = useContext(DropdownMenuRefsContext);
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={mergeRefs(
          ref,
          refs?.contentRef as RefObject<HTMLDivElement | null> | undefined
        )}
        sideOffset={sideOffset}
        {...{ [ROOT_ATTR]: refs?.rootId }}
        data-dropdown-menu=""
        className={cn(
          'z-50 w-72 rounded-xl border-opacity-15 p-1',
          'text-foreground shadow-sm outline-none',
          'bg-surface border border-border-strong',
          'max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto',
          'flex flex-col',
          'origin-[--radix-dropdown-menu-content-transform-origin]',
          fit && 'w-fit',
          className
        )}
        onWheel={(event) => {
          event.stopPropagation();
          onWheel?.(event);
        }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

/** Content without Portal wrapper. Use when the dropdown must stay inside its
 *  parent DOM tree (e.g. notification windows with setIgnoreMouseEvents). */
export const DropdownMenuContentInline = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 w-fit rounded-xl p-1',
      'text-foreground shadow-sm outline-none',
      'bg-surface border border-border-strong',
      'flex flex-col gap-1',
      'origin-[--radix-dropdown-menu-content-transform-origin]',
      'motion-ease-out',
      'data-[state=open]:motion-duration-200',
      'data-[state=open]:motion-scale-in-95',
      'data-[state=open]:motion-opacity-in-0',
      'data-[state=closed]:motion-duration-150',
      'data-[state=closed]:motion-scale-out-95',
      'data-[state=closed]:motion-opacity-out-0',
      className
    )}
    {...props}
  />
));
DropdownMenuContentInline.displayName = 'DropdownMenuContentInline';

export const DropdownMenuItemReason = ({
  children,
}: {
  children: ReactNode;
}) => (
  <span className="basis-full pl-[1.625rem] text-xs text-foreground-muted">
    {children}
  </span>
);

export const DropdownMenuItem = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> &
    VariantProps<typeof itemVariants> & {
      inset?: boolean;
      disabledReason?: string;
    }
>(
  (
    {
      className,
      inset,
      variant,
      textSize,
      disabledReason,
      disabled,
      children,
      ...props
    },
    ref
  ) => (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        itemVariants({ variant, textSize }),
        inset && 'pl-8',
        disabledReason && 'flex-wrap',
        'data-[selected=true]:bg-surface-active data-[selected=true]:text-foreground',
        className
      )}
      {...props}
      disabled={Boolean(disabledReason) || disabled}
    >
      {children}
      {disabledReason && (
        <DropdownMenuItemReason>{disabledReason}</DropdownMenuItemReason>
      )}
    </DropdownMenuPrimitive.Item>
  )
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

export const DropdownMenuCheckboxItem = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'text-foreground relative flex select-none items-center rounded-sm px-3 py-1.5 text-sm outline-none focus:outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-secondary-hover data-[highlighted]:bg-secondary-hover data-[highlighted]:text-foreground [&>svg]:size-4 [&>svg]:shrink-0',
      'data-[selected=true]:bg-secondary-active',
      className
    )}
    checked={checked}
    {...props}
  >
    {children}
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icons.check className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

export const DropdownMenuRadioItem = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.RadioItem>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'relative flex select-none items-center rounded-md px-3 py-2 text-sm outline-none focus:outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-secondary-hover data-[highlighted]:bg-secondary-hover data-[highlighted]:text-foreground',
      className
    )}
    {...props}
  >
    {children}
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icons.check className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

export const DropdownMenuLabel = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      'px-3 py-2 text-sm text-foreground-muted',
      inset && 'pl-8',
      className
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

export const DropdownMenuSeparator = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(
      'mx-3 my-1 h-px shrink-0 bg-border dark:bg-border-strong',
      className
    )}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

export const DropdownMenuShortcut = ({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn('ml-auto text-sm tracking-widest opacity-60', className)}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

interface DropdownMenuItemWithConfirmProps extends Omit<
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>,
  'onClick'
> {
  onConfirm: () => void;
  icon?: LBIcon;
  iconClass?: string;
  confirmIcon?: LBIcon;
  children: ReactNode;
  confirmLabel?: string;
  ariaLabel?: string;
  disabledReason?: string;
}

export const DropdownMenuItemWithConfirm = forwardRef<
  ComponentRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemWithConfirmProps
>(
  (
    {
      onConfirm,
      icon: Icon = Icons.trash,
      confirmIcon: ConfirmIcon = Icons.check,
      children,
      confirmLabel = 'Confirm?',
      className,
      iconClass,
      ariaLabel,
      disabledReason,
      ...props
    },
    ref
  ) => {
    const [confirm, setConfirm] = useState(false);

    useEffect(() => {
      if (!confirm) return;

      const timeout = setTimeout(() => setConfirm(false), 2000);

      return () => clearTimeout(timeout);
    }, [confirm]);

    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        disabled={disabledReason ? true : props.disabled}
        className={cn(
          itemVariants({ variant: 'default' }),
          'group',
          disabledReason && 'flex-wrap',
          className
        )}
        aria-label={confirm ? confirmLabel : ariaLabel}
        onClick={(e) => {
          if (!confirm) {
            e.preventDefault();
            e.stopPropagation();
            setConfirm(true);
            return;
          }

          onConfirm();
        }}
        {...props}
      >
        <span
          className={cn(
            'opacity-75 group-hover:text-red-400',
            confirm && [
              'text-red-400',
              'motion-scale-in-50 motion-opacity-in-0 motion-duration-100',
            ],
            iconClass
          )}
        >
          {confirm ? (
            <ConfirmIcon className="size-3.5" />
          ) : (
            <Icon className="size-3.5" />
          )}
        </span>
        <span
          className={cn(
            'text-foreground group-hover:text-red-400',
            confirm && [
              'text-red-400',
              'motion-opacity-in-0 motion-duration-100',
            ]
          )}
        >
          {confirm ? confirmLabel : children}
        </span>
        {disabledReason && (
          <DropdownMenuItemReason>{disabledReason}</DropdownMenuItemReason>
        )}
      </DropdownMenuPrimitive.Item>
    );
  }
);

DropdownMenuItemWithConfirm.displayName = 'DropdownMenuItemWithConfirm';
